import { apiClient } from './client'
import type { ApiResponse } from '@/types/api'

export interface GameResponse {
  gameKey: string
  published: boolean
  linkedLessonCount: number
  createdAt: string
  updatedAt: string
}

export interface GameSyncRequest {
  gameKeys: string[]
}

export interface GameContentPathNode {
  contentId: number
  title: string | null
}

export type GameLessonPathStatus = 'ACTIVE' | 'STALE'

export interface GameLessonMapping {
  mappingId: number | null
  curriculum: GameContentPathNode
  course: GameContentPathNode
  module: GameContentPathNode
  lesson: GameContentPathNode
  sortOrder: number
  status: GameLessonPathStatus
  staleReason: string | null
}

export interface GameLessonPathRequest {
  curriculumContentId: number
  courseContentId: number
  moduleContentId: number
  lessonContentId: number
  sortOrder: number
}

export interface GameLessonMappingSaveRequest {
  mappings: GameLessonPathRequest[]
}

export interface GameLessonPathGame {
  gameKey: string
  published: boolean
}

export const gameApi = {
  getAll: async (): Promise<GameResponse[]> => {
    const response = await apiClient.get<ApiResponse<GameResponse[]>>('/admin/games')
    if (!response.data.data) {
      throw new Error('Failed to fetch games')
    }
    return response.data.data
  },

  sync: async (request: GameSyncRequest): Promise<GameResponse[]> => {
    const response = await apiClient.post<ApiResponse<GameResponse[]>>(
      '/admin/games/sync',
      request
    )
    if (!response.data.data) {
      throw new Error('Failed to sync games')
    }
    return response.data.data
  },

  togglePublish: async (gameKey: string): Promise<GameResponse> => {
    const response = await apiClient.patch<ApiResponse<GameResponse>>(
      `/admin/games/${encodeURIComponent(gameKey)}/publish`
    )
    if (!response.data.data) {
      throw new Error('Failed to toggle game publish status')
    }
    return response.data.data
  },

  getLessonMappings: async (gameKey: string): Promise<GameLessonMapping[]> => {
    const response = await apiClient.get<ApiResponse<GameLessonMapping[]>>(
      `/admin/games/${encodeURIComponent(gameKey)}/lesson-mappings`
    )
    return response.data.data ?? []
  },

  replaceLessonMappings: async (
    gameKey: string,
    request: GameLessonMappingSaveRequest
  ): Promise<GameLessonMapping[]> => {
    const response = await apiClient.put<ApiResponse<GameLessonMapping[]>>(
      `/admin/games/${encodeURIComponent(gameKey)}/lesson-mappings`,
      request
    )
    return response.data.data ?? []
  },

  getGamesByLessonPath: async (
    curriculumContentId: number,
    courseContentId: number,
    moduleContentId: number,
    lessonContentId: number
  ): Promise<GameLessonPathGame[]> => {
    const response = await apiClient.get<ApiResponse<GameLessonPathGame[]>>(
      '/admin/games/by-lesson-path',
      {
        params: {
          curriculumContentId,
          courseContentId,
          moduleContentId,
          lessonContentId,
        },
      }
    )
    return response.data.data ?? []
  },
}
